import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type {
  CompleteRegistrationBody,
  OtpRequestResult,
  TelegramRequestResult,
  UpdateProfileBody,
  UserDto,
} from '@lex/contracts';
import { apiClient, tokenStorage } from './api';

interface AuthState {
  user: UserDto | null;
  loading: boolean;
  isAuthenticated: boolean;
  requestPhoneCode: (phone: string) => Promise<OtpRequestResult>;
  verifyPhoneCode: (phone: string, code: string) => Promise<UserDto>;
  requestEmailCode: (email: string) => Promise<OtpRequestResult>;
  verifyEmailCode: (email: string, code: string) => Promise<UserDto>;
  requestTelegramCode: () => Promise<TelegramRequestResult>;
  verifyTelegramCode: (token: string, code: string) => Promise<UserDto>;
  updateProfile: (body: UpdateProfileBody) => Promise<void>;
  refresh: () => Promise<void>;
  completeRegistration: (body: CompleteRegistrationBody) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserDto | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tokenStorage.getAccessToken()) {
      setLoading(false);
      return;
    }
    apiClient.auth
      .me()
      .then(setUser)
      .catch(() => tokenStorage.clear())
      .finally(() => setLoading(false));
  }, []);

  const applySession = useCallback(async (): Promise<UserDto> => {
    const profile = await apiClient.auth.me();
    setUser(profile);
    return profile;
  }, []);

  const requestPhoneCode = useCallback(
    (phone: string) => apiClient.auth.requestPhoneCode({ phone }),
    []
  );

  const verifyPhoneCode = useCallback(
    async (phone: string, code: string) => {
      const res = await apiClient.auth.verifyPhoneCode({ phone, code });
      tokenStorage.setTokens(res);
      return applySession();
    },
    [applySession]
  );

  const requestEmailCode = useCallback(
    (email: string) => apiClient.auth.requestEmailCode({ email }),
    []
  );

  const verifyEmailCode = useCallback(
    async (email: string, code: string) => {
      const res = await apiClient.auth.verifyEmailCode({ email, code });
      tokenStorage.setTokens(res);
      return applySession();
    },
    [applySession]
  );

  const requestTelegramCode = useCallback(
    () => apiClient.auth.requestTelegramCode(),
    []
  );

  const verifyTelegramCode = useCallback(
    async (token: string, code: string) => {
      const res = await apiClient.auth.verifyTelegramCode({ token, code });
      tokenStorage.setTokens(res);
      return applySession();
    },
    [applySession]
  );

  const updateProfile = useCallback(async (body: UpdateProfileBody) => {
    setUser(await apiClient.auth.updateProfile(body));
  }, []);

  const refresh = useCallback(async () => {
    setUser(await apiClient.auth.me());
  }, []);

  const completeRegistration = useCallback(async (body: CompleteRegistrationBody) => {
    setUser(await apiClient.auth.completeRegistration(body));
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = tokenStorage.getRefreshToken() ?? undefined;
    await apiClient.auth.logout(refreshToken).catch(() => undefined);
    tokenStorage.clear();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: Boolean(user),
        requestPhoneCode,
        verifyPhoneCode,
        requestEmailCode,
        verifyEmailCode,
        requestTelegramCode,
        verifyTelegramCode,
        updateProfile,
        refresh,
        completeRegistration,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
